import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Loader2, Smartphone, BadgeEuro } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const PROBLEMS = [
  { value: "ecran", label: "Écran cassé / tactile" },
  { value: "batterie", label: "Batterie / charge" },
  { value: "camera", label: "Caméra / photos" },
  { value: "connecteur", label: "Connecteur de charge" },
  { value: "vitre_arriere", label: "Vitre arrière" },
  { value: "haut_parleur", label: "Haut-parleur / micro" },
  { value: "autre", label: "Autre panne" },
];

const PRICE_TYPES = [
  { value: "from", label: "À partir de" },
  { value: "fixed", label: "Prix fixe" },
  { value: "diagnostic", label: "Devis après diagnostic" },
];

const EMPTY_PRICE = { problem: "ecran", model_id: "", price_type: "from", price: "", duration: "" };

export default function AdminPrices() {
  const [models, setModels] = useState([]);
  const [prices, setPrices] = useState(null);
  const [modelForm, setModelForm] = useState({ brand: "", name: "" });
  const [priceForm, setPriceForm] = useState(null); // {id?}
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/repair-models").then((r) => setModels(r.data)).catch(() => {});
    api.get("/admin/repair-prices").then((r) => setPrices(r.data)).catch(() => setPrices([]));
  };
  useEffect(() => { load(); }, []);

  const modelName = (id) => {
    if (!id) return "Tous modèles";
    const m = models.find((x) => x.id === id);
    return m ? `${m.brand} ${m.name}` : "Modèle supprimé";
  };

  const addModel = async (e) => {
    e.preventDefault();
    if (!modelForm.brand.trim() || !modelForm.name.trim()) { toast.error("Marque et modèle requis"); return; }
    try {
      await api.post("/admin/repair-models", modelForm);
      setModelForm({ brand: "", name: "" });
      toast.success("Modèle ajouté");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteModel = async (m) => {
    if (!window.confirm(`Supprimer le modèle « ${m.brand} ${m.name} » ?`)) return;
    await api.delete(`/admin/repair-models/${m.id}`);
    toast.success("Modèle supprimé");
    load();
  };

  const savePrice = async () => {
    setSaving(true);
    const payload = {
      problem: priceForm.problem,
      model_id: priceForm.model_id || null,
      price_type: priceForm.price_type,
      price: priceForm.price_type === "diagnostic" ? null : parseFloat(priceForm.price) || 0,
      duration: priceForm.duration,
      label: PROBLEMS.find((p) => p.value === priceForm.problem)?.label,
    };
    try {
      if (priceForm.id) await api.put(`/admin/repair-prices/${priceForm.id}`, payload);
      else await api.post("/admin/repair-prices", payload);
      toast.success("Tarif enregistré");
      setPriceForm(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const deletePrice = async (p) => {
    if (!window.confirm("Supprimer ce tarif ?")) return;
    await api.delete(`/admin/repair-prices/${p.id}`);
    toast.success("Tarif supprimé");
    load();
  };

  const priceLabel = (p) => (p.price_type === "diagnostic" ? "Devis après diagnostic" : p.price_type === "fixed" ? `${p.price} €` : `À partir de ${p.price} €`);

  if (priceForm) {
    const set = (k, v) => setPriceForm((f) => ({ ...f, [k]: v }));
    return (
      <div className="mx-auto max-w-xl" data-testid="price-editor">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-2xl font-extrabold text-slate-900">{priceForm.id ? "Modifier le tarif" : "Nouveau tarif"}</h1>
          <button data-testid="price-editor-close" onClick={() => setPriceForm(null)} className="rounded-full bg-slate-100 p-2.5 text-slate-500"><X className="h-5 w-5" /></button>
        </div>
        <div className="card-oxy mt-5 space-y-4 p-5">
          <div>
            <label className="label-oxy">Type de panne</label>
            <select data-testid="price-problem" value={priceForm.problem} onChange={(e) => set("problem", e.target.value)} className="input-oxy">
              {PROBLEMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label-oxy">Modèle concerné</label>
            <select data-testid="price-model" value={priceForm.model_id || ""} onChange={(e) => set("model_id", e.target.value)} className="input-oxy">
              <option value="">Tous modèles (tarif générique)</option>
              {models.map((m) => <option key={m.id} value={m.id}>{m.brand} {m.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label-oxy">Type de tarif</label>
            <select data-testid="price-type" value={priceForm.price_type} onChange={(e) => set("price_type", e.target.value)} className="input-oxy">
              {PRICE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          {priceForm.price_type !== "diagnostic" && (
            <div>
              <label className="label-oxy">Prix (€)</label>
              <input data-testid="price-value" type="number" min="0" step="0.01" inputMode="decimal" value={priceForm.price} onChange={(e) => set("price", e.target.value)} className="input-oxy" />
            </div>
          )}
          <div>
            <label className="label-oxy">Durée indicative</label>
            <input data-testid="price-duration" value={priceForm.duration} onChange={(e) => set("duration", e.target.value)} className="input-oxy" placeholder="Ex : 45 min – 1h30" />
          </div>
          <button data-testid="price-save-btn" onClick={savePrice} disabled={saving} className="btn-gold w-full disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Enregistrer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-prices-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-extrabold text-slate-900">Tarifs réparation</h1>
        <button data-testid="price-create-btn" onClick={() => setPriceForm({ ...EMPTY_PRICE })} className="btn-gold !px-5 !py-2.5 text-sm">
          <Plus className="h-4 w-4" /> Nouveau tarif
        </button>
      </div>

      {prices === null ? (
        <div className="mt-5 space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-16 animate-pulse rounded-2xl bg-slate-100" />)}</div>
      ) : (
        <ul className="mt-5 space-y-2.5" data-testid="prices-list">
          {prices.map((p) => (
            <li key={p.id} data-testid={`price-row-${p.id}`} className="card-oxy flex items-center justify-between gap-3 p-4">
              <div>
                <p className="font-display font-bold text-slate-900">{PROBLEMS.find((x) => x.value === p.problem)?.label || p.label}</p>
                <p className="mt-0.5 text-xs text-slate-500">{modelName(p.model_id)}{p.duration ? ` · ${p.duration}` : ""}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="rounded-full bg-gold-soft px-3 py-1.5 text-sm font-bold text-gold-dark">{priceLabel(p)}</span>
                <button data-testid={`price-edit-${p.id}`} onClick={() => setPriceForm({ ...p, model_id: p.model_id || "", price: p.price ?? "" })}
                  className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-gold-soft">Modifier</button>
                <button data-testid={`price-delete-${p.id}`} onClick={() => deletePrice(p)}
                  className="rounded-xl bg-slate-100 p-2.5 text-slate-500 hover:bg-red-100 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </li>
          ))}
          {prices.length === 0 && <p className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">Aucun tarif configuré.</p>}
        </ul>
      )}

      <div className="mt-10">
        <h2 className="flex items-center gap-2 font-display text-xl font-bold text-slate-900"><Smartphone className="h-5 w-5 text-gold-dark" /> Modèles pris en charge</h2>
        <p className="mt-1 text-xs text-slate-500">Cette liste alimente le questionnaire de devis.</p>
        <form onSubmit={addModel} className="card-oxy mt-4 flex flex-col gap-3 p-4 sm:flex-row">
          <input data-testid="model-brand-input" value={modelForm.brand} onChange={(e) => setModelForm((f) => ({ ...f, brand: e.target.value }))}
            placeholder="Marque (ex : Apple)" className="input-oxy flex-1" />
          <input data-testid="model-name-input" value={modelForm.name} onChange={(e) => setModelForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Modèle (ex : iPhone 15)" className="input-oxy flex-1" />
          <button type="submit" data-testid="model-add-btn" className="btn-ink !px-5 !py-3 text-sm"><Plus className="h-4 w-4" /> Ajouter</button>
        </form>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2" data-testid="models-list">
          {models.map((m) => (
            <li key={m.id} data-testid={`model-row-${m.id}`} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm">
              <span className="font-semibold text-slate-800">{m.brand} {m.name}</span>
              <button data-testid={`model-delete-${m.id}`} onClick={() => deleteModel(m)} className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
